import { useCallback, useRef, useState } from 'react';
import { productUpdateReadId } from '../product-update-version';
import type { AppRoute } from '../routes';
import './WhatsNew.css';
import { deferWorkspace } from '../defer-workspace';

const UpdateContent = deferWorkspace(() => import('./ProductUpdateContent'), {
  loadingMessage: 'Loading updates…',
  errorMessage: 'Updates could not be loaded. Check your connection and try again.',
  offerReload: false,
});

const SEEN_KEY = 'resilience-crm:product-updates:last-seen';

function lastReadUpdate() {
  try {
    return window.localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

/** A local editorial feed. Loads code only; never requests or changes CRM records. */
export function WhatsNew({
  profitabilityAvailable = false,
  documentsAvailable = false,
  visibleRoutes = [],
  onRouteIntent,
}: {
  profitabilityAvailable?: boolean;
  documentsAvailable?: boolean;
  visibleRoutes?: readonly AppRoute[];
  onRouteIntent?: ((route: AppRoute) => void) | undefined;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const [readId, setReadId] = useState(lastReadUpdate);
  const latest = productUpdateReadId(profitabilityAvailable, documentsAvailable);
  const unread = readId !== latest;
  const markRead = useCallback(() => setReadId(latest), [latest]);
  const [open, setOpen] = useState(false);

  function close() {
    dialog.current?.close();
  }

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className="whats-new-trigger"
        title="What's new"
        aria-label={unread ? "What's new — new updates" : "What's new"}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="product-updates-dialog"
        onClick={() => {
          dialog.current?.showModal();
          setOpen(true);
        }}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="m10 3 2.2 6.8L19 12l-6.8 2.2L10 21l-2.2-6.8L1 12l6.8-2.2L10 3Zm9-2 1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3Z" />
        </svg>
        <span className="whats-new-label">What's new</span>
        {unread ? <span className="whats-new-dot" aria-hidden="true" /> : null}
      </button>
      <dialog
        ref={dialog}
        id="product-updates-dialog"
        className="whats-new-dialog"
        aria-labelledby="product-updates-heading"
        onCancel={(event) => {
          event.preventDefault();
          close();
        }}
        onClose={() => {
          setOpen(false);
          trigger.current?.focus();
        }}
        onClick={(event) => {
          if (event.target !== event.currentTarget) return;
          const bounds = event.currentTarget.getBoundingClientRect();
          if (
            event.clientX < bounds.left ||
            event.clientX > bounds.right ||
            event.clientY < bounds.top ||
            event.clientY > bounds.bottom
          )
            close();
        }}
      >
        <div className="whats-new-heading">
          <div>
            <p className="whats-new-eyebrow">Resilience CRM</p>
            <h2 id="product-updates-heading">What's new</h2>
          </div>
          <button type="button" className="whats-new-close" onClick={close} autoFocus>
            <span className="sr-only">Close updates</span>
            <span aria-hidden="true">×</span>
          </button>
        </div>
        {open ? (
          <UpdateContent
            onRead={markRead}
            profitabilityAvailable={profitabilityAvailable}
            documentsAvailable={documentsAvailable}
            visibleRoutes={visibleRoutes}
            onRouteIntent={onRouteIntent}
            onNavigate={close}
          />
        ) : null}
      </dialog>
    </>
  );
}
